import Vue from 'vue'
import { boot } from 'quasar/wrappers'
import { Route, NavigationGuardNext } from 'vue-router'
import { FUSIONAUTH_USER, TOKEN_NAME, TOKEN_REFRESH_NAME } from 'src/lib/constants'
import { getSupportedBrowserVersion } from 'src/lib/browser'
import { PermissionRole } from 'able-lib'
import { closeApolloWebSocket } from 'boot/apollo'
import { sendBugsnagReport } from 'boot/bugsnag'
import { fusionClient } from 'boot/fusionAuth'

declare const localStorage

export default boot(async ({ store, router }: any) => {
  const browserVersion = getSupportedBrowserVersion()

  router.beforeEach(async (to: Route, from: Route, next: NavigationGuardNext) => {
    let token = localStorage.getItem(TOKEN_NAME)
    let user = JSON.parse(localStorage.getItem(FUSIONAUTH_USER))
    let refreshToken = localStorage.getItem(TOKEN_REFRESH_NAME)

    function nextTo () {
      if (to.matched.some(record => record.meta.requiresEditor)) {
        if (store.getters['user/hasRole'](PermissionRole.EDITOR)) {
          next()
        } else {
          next({
            path: '/dashboard',
            replace: true
          })
        }
      } else if (to.matched.some(record => record.meta.requiresAdministrator)) {
        if (store.getters['user/hasRole'](PermissionRole.ADMINISTRATOR)) {
          next()
        } else {
          next({
            path: '/dashboard',
            replace: true
          })
        }
      } else {
        next()
      }
    }

    async function checkPolicy () {
      const userID = store.getters['user/getUser']
      const policies = await store.dispatch('message/loadUnAcknowledgePolicies', userID?.id)
      if (policies.length !== 0) {
        if (to.path === '/policy') {
          nextTo()
        } else {
          next({
            path: '/policy',
            replace: true
          })
        }
      } else {
        nextTo()
      }
    }

    if (!browserVersion.isSupported) {
      if (to.path !== '/notsupported') {
        next({
          path: '/notsupported',
          replace: true
        })
      } else {
        nextTo()
      }
    } else if (to.matched.some(record => record.meta.requiresAuth)) {
      if (!token) {
        await store.dispatch('user/fusionAuthLogout')
        next({
          path: '/login',
          query: { nextUrl: to.fullPath },
          replace: true
        })
      } else {
        let result = await store.dispatch('user/validateJWT', token)
        if (result.expired) {
          if (refreshToken) {
            result = await store.dispatch('user/retrieveNewAccessToken', {
              refreshToken,
              token
            })
            if (!result.success) {
              next({
                path: '/login',
                query: { nextUrl: to.fullPath },
                replace: true
              })
            } else {
              nextTo()
            }
          } else {
            await store.dispatch('user/fusionAuthLogout')
            next({
              path: '/login',
              query: { nextUrl: to.fullPath },
              replace: true
            })
          }
        } else {
          const isValidToken = await fusionClient.validateJWT(token)
          try {
            const user = await fusionClient.retrieveUserUsingJWT(token)
            const currentUserEmail = user.response.user.email
            await store.dispatch('user/getUserInfo', { email: currentUserEmail })
            await store.dispatch('lesson/upcomingLesson')
            const getUser = store.getters['user/getUser']

            await store.dispatch('local/getCurrentYear')
            await store.dispatch('local/fetchRoles')
            await store.dispatch('local/fetchLocations')
            await store.dispatch('local/fetchLevels')
            await store.dispatch('local/fetchSubjects')
            store.dispatch('local/getNotifications', getUser.id)

            if (isValidToken.statusCode == 200) {
              try {
                if (!user.response.user.active) {
                  await router.push({
                    path: '/accountdisabled'
                  }).catch(err => { if (err && err.name !== 'NavigationDuplicated') console.error(err) })
                }
              } catch (e) {
                sendBugsnagReport(`${currentUserEmail}: ${e}`)
              }
            }
          } catch (e) {
            console.error(e)
            await router.push({
              path: '/notregistered',
              query: {
                email: user.email
              }
            }).catch(err => { if (err && err.name !== 'NavigationDuplicated') console.error(err) })
            store.dispatch('user/logout')
          }
          // metaEditor
          await checkPolicy()
        }
      }
    } else if (to.path === '/logout') {
      await store.dispatch('user/fusionAuthLogout')
      closeApolloWebSocket()
      next({
        path: '/login'
      })
    } else {
      nextTo()
    }
  })
})
